const SEARCH_V2_CHECKLIST_STORAGE_KEY = "searchV2ChecklistByJobIdV2";
const CHECKLIST_ITEM_KEYS = [
  "keywords",
  "filters",
  "tuning",
] as const;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const getCompletedItemsByJobId = (): Record<string, string[]> => {
  try {
    const storedValue = localStorage.getItem(SEARCH_V2_CHECKLIST_STORAGE_KEY);
    if (!storedValue) return {};

    const parsedValue: unknown = JSON.parse(storedValue);
    if (!isRecord(parsedValue)) return {};

    return Object.fromEntries(
      Object.entries(parsedValue).map(([jobId, completedItems]) => [
        jobId,
        Array.isArray(completedItems)
          ? completedItems.filter(
              (item): item is string =>
                typeof item === "string" &&
                CHECKLIST_ITEM_KEYS.some((key) => key === item),
            )
          : [],
      ]),
    );
  } catch {
    return {};
  }
};

const setCompletedItemsByJobId = (
  completedItemsByJobId: Record<string, string[]>,
) => {
  try {
    localStorage.setItem(
      SEARCH_V2_CHECKLIST_STORAGE_KEY,
      JSON.stringify(completedItemsByJobId),
    );
  } catch {
    // Keep checklist progress in memory when storage is unavailable.
  }
};

export const searchV2ChecklistStorage = {
  add: (jobId: string, item: string): string[] => {
    const completedItemsByJobId = getCompletedItemsByJobId();
    const completedItems = completedItemsByJobId[jobId] ?? [];
    if (completedItems.includes(item)) return completedItems;

    const nextCompletedItems = [...completedItems, item];
    completedItemsByJobId[jobId] = nextCompletedItems;
    setCompletedItemsByJobId(completedItemsByJobId);
    return nextCompletedItems;
  },
  get: (jobId: string): string[] =>
    getCompletedItemsByJobId()[jobId] ?? [],
  toggle: (jobId: string, item: string): string[] => {
    const completedItemsByJobId = getCompletedItemsByJobId();
    const completedItems = completedItemsByJobId[jobId] ?? [];
    const nextCompletedItems = completedItems.includes(item)
      ? completedItems.filter((completedItem) => completedItem !== item)
      : [...completedItems, item];
    completedItemsByJobId[jobId] = nextCompletedItems;
    setCompletedItemsByJobId(completedItemsByJobId);
    return nextCompletedItems;
  },
};
